// New UI fleet sidebar (new_ui_enabled flag): the persistent left rail that
// lists every aircraft with its recent flights, so switching flights is one
// click from anywhere. Also renders the topbar breadcrumb and the first-run
// "add your first aircraft" hero, since all three read the same selection
// state. Everything is injected from app.ts; this module owns only the
// sidebar/breadcrumb/hero DOM and its delegated events.
//
// While the flag is off (old UI) every render is a cheap no-op and the
// markup containers stay empty, so the old navigation is untouched.

import { orderFleetForDisplay, orderFlightsForDisplay } from "./display-preferences.js";

type FlightEntry = {
  id: string;
  aircraft?: string;
  origin_ident?: string | null;
  destination_ident?: string | null;
  // Used to order the fleet by most recent activity.
  flight_start_at?: string | null;
  flight_start_local?: string | null;
};

type FleetSidebarDeps = {
  escapeHtml: (value: unknown) => string;
  aircraftCreatedAt: (aircraft: string) => string | null;
  newUiFeatureEnabled: () => boolean;
  aircraftFlightManifestReady: () => boolean;
  ensureAircraftFlightManifestLoaded: () => Promise<unknown>;
  allAircraftOptions: () => string[];
  aircraftWorkspacePlanLocked: (aircraft: string) => boolean;
  selectedAircraftWorkspace: () => string;
  getSelectedFlight: () => FlightEntry | null;
  getSelectedAircraftPanel: () => string;
  aircraftFlights: (aircraft: string) => FlightEntry[];
  latestFirstComparator: (left: FlightEntry, right: FlightEntry) => number;
  isSupportedFlight: (entry: FlightEntry) => boolean;
  flightDisplayTitle: (entry: FlightEntry) => string;
  flightDateLabel: (entry: FlightEntry) => string;
  aircraftDisplayLabel: (value: string) => string;
  getAircraftProfiles: () => Record<string, any> | null;
  aircraftMakeModelLabel: (profile: any) => string | null;
  aircraftPhotoSource: (profile: any) => string | null;
  defaultArtRestUrl?: (profile: any) => string | null;
  defaultArtHoverUrl?: (profile: any) => string | null;
  aircraftPhotoFallbackLabel: (value: string) => string;
  openFleetSidebarFlight: (entry: FlightEntry) => Promise<void>;
  openFleetSidebarAircraft: (aircraft: string) => void;
  openFleetSidebarPanel: (aircraft: string, panel: string) => boolean | void;
  showAppToast: (message: string) => void;
};

const MULTI_AIRCRAFT_RECENT_FLIGHTS = 5;
const SINGLE_AIRCRAFT_RECENT_FLIGHTS = 12;
const SEARCH_RESULT_FLIGHTS_PER_AIRCRAFT = 30;
const RAIL_COLLAPSED_STORAGE_KEY = "aeroInsightFleetRailCollapsed";

export function createFleetSidebar({
  escapeHtml,
  aircraftCreatedAt,
  newUiFeatureEnabled,
  aircraftFlightManifestReady,
  ensureAircraftFlightManifestLoaded,
  allAircraftOptions,
  aircraftWorkspacePlanLocked,
  selectedAircraftWorkspace,
  getSelectedFlight,
  getSelectedAircraftPanel,
  aircraftFlights,
  latestFirstComparator,
  isSupportedFlight,
  flightDisplayTitle,
  flightDateLabel,
  aircraftDisplayLabel,
  getAircraftProfiles,
  aircraftMakeModelLabel,
  aircraftPhotoSource,
  defaultArtRestUrl = () => null,
  defaultArtHoverUrl = () => null,
  aircraftPhotoFallbackLabel,
  openFleetSidebarFlight,
  openFleetSidebarAircraft,
  openFleetSidebarPanel,
  showAppToast,
}: FleetSidebarDeps) {
  // Accordion open state: at most one aircraft group is expanded at a time
  // (an active search shows every matching group open regardless), persisted
  // across reloads. null means everything is deliberately closed.
  const OPEN_GROUPS_STORAGE_KEY = "aeroInsightFleetOpenAircraft";
  let loadingFlightId: string | null = null;
  let openTail: string | null = null;
  let openTailInitialized = false;
  try {
    const raw = localStorage.getItem(OPEN_GROUPS_STORAGE_KEY);
    if (raw !== null) {
      const stored = JSON.parse(raw);
      if (typeof stored?.open === "string" || stored?.open === null) {
        openTail = stored.open;
      } else if (Array.isArray(stored?.expanded)) {
        // Migrate the earlier multi-open shape: keep the first open group.
        openTail = typeof stored.expanded[0] === "string" ? stored.expanded[0] : null;
      }
      openTailInitialized = true;
    }
  } catch {}

  function setOpenTail(next: string | null) {
    openTail = next;
    openTailInitialized = true;
    try {
      localStorage.setItem(OPEN_GROUPS_STORAGE_KEY, JSON.stringify({ open: openTail }));
    } catch {}
  }

  let searchQuery = "";
  let wired = false;
  let railCollapsed = false;
  try {
    railCollapsed = localStorage.getItem(RAIL_COLLAPSED_STORAGE_KEY) === "1";
  } catch {}

  function setRailCollapsed(next: boolean) {
    railCollapsed = next;
    try {
      localStorage.setItem(RAIL_COLLAPSED_STORAGE_KEY, next ? "1" : "0");
    } catch {}
    renderFleetSidebar();
  }

  function sidebarElement() {
    return document.querySelector<HTMLElement>("#fleetSidebar");
  }

  function crumbsElement() {
    return document.querySelector<HTMLElement>("#newUiCrumbs");
  }

  function firstRunElement() {
    return document.querySelector<HTMLElement>("#newUiFirstRun");
  }

  function closeFleetDrawer() {
    document.body.classList.remove("fleet-drawer-open");
  }

  // One in-flight manifest kick at a time. A load that settles without
  // flipping the ready flag must not re-render (that would kick again in a
  // loop); a later render can still retry after a failure.
  let manifestKickInFlight = false;
  function kickManifestLoad() {
    if (manifestKickInFlight || aircraftFlightManifestReady()) return;
    manifestKickInFlight = true;
    void ensureAircraftFlightManifestLoaded()
      .then(() => {
        if (aircraftFlightManifestReady()) renderFleetSidebar();
      })
      .catch(() => {})
      .finally(() => {
        manifestKickInFlight = false;
      });
  }

  function latestFirstFlights(aircraft: string) {
    return [...aircraftFlights(aircraft)].sort(latestFirstComparator);
  }

  function groupIsOpen(aircraft: string, singleMode: boolean) {
    if (singleMode) return true;
    return aircraft === openTail;
  }

  function flightSearchText(entry: FlightEntry) {
    return [
      flightDisplayTitle(entry),
      flightDateLabel(entry),
      (entry as { custom_name?: string }).custom_name ?? "",
      entry?.origin_ident ?? "",
      entry?.destination_ident ?? "",
    ].join(" ").toLowerCase();
  }

  function flightRouteLabel(entry: FlightEntry) {
    const origin = String(entry?.origin_ident ?? "").trim();
    const destination = String(entry?.destination_ident ?? "").trim();
    if (origin && destination) return origin === destination ? `${origin} local` : `${origin} - ${destination}`;
    return origin || destination || "";
  }

  function flightItemHtml(entry: FlightEntry, aircraft: string) {
    // Highlight the loaded flight only while its dashboard is showing; on the
    // aircraft pages (Flights/Uploads/...) no rail flight is "current".
    const loading = loadingFlightId === entry.id;
    const active = loading || (!loadingFlightId && getSelectedFlight()?.id === entry.id && getSelectedAircraftPanel() === "dashboard");
    const supported = isSupportedFlight(entry);
    const route = flightRouteLabel(entry);
    const classes = ["fleet-flight-item"];
    if (active) classes.push("is-active");
    if (loading) classes.push("is-loading");
    if (!supported) classes.push("is-unsupported");
    // The rail always leads with the date; a user-set flight name takes the
    // second line, replacing the from-to route (the route stays reachable
    // through the hover title below).
    const customName = String((entry as { custom_name?: string }).custom_name ?? "").trim();
    const dateLabel = flightDateLabel(entry) || flightDisplayTitle(entry);
    const secondLine = customName || route;
    // Collapsed rail shows a compact date ("Aug 18"); CSS swaps which span shows.
    const shortLabel = dateLabel.replace(/,\s*\d{4}\s*$/, "") || dateLabel;
    return `<button class="${classes.join(" ")}" type="button"
      data-fleet-flight="${escapeHtml(entry.id)}" data-fleet-flight-aircraft="${escapeHtml(aircraft)}"
      ${supported ? "" : 'disabled title="This flight\'s format is not supported for review yet"'}
      ${loading ? 'aria-busy="true"' : ""}
      ${active ? 'aria-current="true"' : ""} title="${escapeHtml(customName ? `${dateLabel} — ${customName}` : dateLabel)}${route ? ` (${escapeHtml(route)})` : ""}">
      <span class="fleet-flight-date">${escapeHtml(dateLabel)}</span>
      <span class="fleet-flight-short" aria-hidden="true">${escapeHtml(shortLabel)}</span>
      ${secondLine ? `<span class="fleet-flight-route">${escapeHtml(secondLine)}</span>` : ""}
      ${(entry as { is_new?: boolean }).is_new === true
        ? '<span class="row-new-tag fleet-flight-new">New</span>'
        : ""}
    </button>`;
  }

  function aircraftGroupHtml(aircraft: string, singleMode: boolean) {
    const profiles = getAircraftProfiles() ?? {};
    const profile = profiles[aircraft] ?? null;
    const locked = aircraftWorkspacePlanLocked(aircraft);
    const current = aircraft === selectedAircraftWorkspace();
    const tail = aircraftDisplayLabel(aircraft) || aircraft;
    const model = aircraftMakeModelLabel(profile) ?? "";
    const photo = aircraftPhotoSource(profile);
    // latestFirstFlights() is newest-first; the Settings "Flight order"
    // preference flips it. The slice below takes the FIRST N, so the order has
    // to be applied before slicing or "oldest first" would show the newest
    // flights in reverse rather than the oldest ones.
    const flights = orderFlightsForDisplay(latestFirstFlights(aircraft));
    const open = !locked && groupIsOpen(aircraft, singleMode);
    const query = searchQuery.trim().toLowerCase();
    const matching = query
      ? flights.filter((entry) => flightSearchText(entry).includes(query)).slice(0, SEARCH_RESULT_FLIGHTS_PER_AIRCRAFT)
      : flights.slice(0, singleMode ? SINGLE_AIRCRAFT_RECENT_FLIGHTS : MULTI_AIRCRAFT_RECENT_FLIGHTS);
    if (query && !matching.length) return "";

    // Placeholder avatars carry the full tail number; fitAvatarFallbackLabels
    // measures and shrinks the font after render so it always fits.
    const fallbackLabel = tail || aircraftPhotoFallbackLabel(aircraft);
    // No uploaded photo: fall back to the generated default line art (public
    // URLs, so a plain <img src> works), then to the tail-number initials.
    const defaultArt = photo ? null : defaultArtRestUrl(profile);
    // The side/front view stacks above the top-down art and fades in on row
    // hover (CSS .fleet-avatar-art-hover); both are public URLs.
    const defaultArtHover = defaultArt ? defaultArtHoverUrl(profile) : null;
    const avatar = photo
      ? `<span class="fleet-aircraft-avatar"><img src="${escapeHtml(photo)}" alt="" /></span>`
      : defaultArt
        ? `<span class="fleet-aircraft-avatar"><img class="is-default-art" src="${escapeHtml(defaultArt)}" alt="" />${
          defaultArtHover ? `<img class="is-default-art fleet-avatar-art-hover" src="${escapeHtml(defaultArtHover)}" alt="" />` : ""
        }</span>`
        : `<span class="fleet-aircraft-avatar fleet-aircraft-avatar-fallback">${escapeHtml(fallbackLabel)}</span>`;

    // A collapsed group hides its flights, so the head carries a dot whenever
    // the aircraft holds an unreviewed flight. Counted over ALL the aircraft's
    // flights, not just the few rows this group renders, so a new flight
    // further down the list still shows up here.
    const hasNew = flights.some((entry) => (entry as { is_new?: boolean }).is_new === true);

    const classes = ["fleet-aircraft-group"];
    if (open || query) classes.push("is-open");
    if (current) classes.push("is-current");
    if (locked) classes.push("is-locked");
    if (hasNew) classes.push("has-new");

    let html = `<div class="${classes.join(" ")}" data-fleet-group="${escapeHtml(aircraft)}">`;
    html += `<div class="fleet-aircraft-head${photo ? " has-photo" : ""}" role="button" tabindex="0"
      data-fleet-aircraft="${escapeHtml(aircraft)}"
      aria-expanded="${open || Boolean(query)}" ${current ? 'aria-current="true"' : ""}
      ${locked ? 'title="This aircraft is not available on the current plan"' : ""}>
      ${avatar}
      <span class="fleet-aircraft-title">
        <span class="fleet-aircraft-tail">${escapeHtml(tail)}${hasNew
          ? '<span class="fleet-aircraft-new-dot" aria-hidden="true" title="Unreviewed flights"></span>'
          : ""}</span>
        ${model ? `<span class="fleet-aircraft-model">${escapeHtml(model)}</span>` : ""}
      </span>
      ${locked
        ? '<span class="fleet-aircraft-lock" aria-hidden="true">&#128274;</span>'
        : `${singleMode ? "" : `<span class="fleet-aircraft-count">${flights.length}</span>
           <button class="fleet-aircraft-chevron${open || query ? " is-open" : ""}" type="button"
             data-fleet-toggle="${escapeHtml(aircraft)}" aria-expanded="${open || Boolean(query)}"
             aria-label="${open ? "Collapse" : "Expand"} flights for ${escapeHtml(tail)}"
             title="${open ? "Collapse flights" : "Expand flights"}">
             <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
               <path d="M9 6l6 6-6 6" />
             </svg>
           </button>`}`}
    </div>`;
    if (!locked) {
      const uploadButton = (label: string) => `<button class="fleet-upload-mini" type="button"
        data-fleet-upload="${escapeHtml(aircraft)}" aria-label="${escapeHtml(label)} for ${escapeHtml(tail)}" title="${escapeHtml(label)}">
        <svg class="fleet-upload-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
          <path d="M14 3v5h5" />
          <path d="M12 16.5v-6" />
          <path d="M9.5 12.5 12 10l2.5 2.5" />
        </svg>
        <span class="fleet-upload-text">${escapeHtml(label)}</span>
      </button>`;
      html += '<div class="fleet-aircraft-flights">';
      if (!flights.length) {
        html += '<p class="fleet-flights-empty">No flights yet.</p>';
        html += `<div class="fleet-aircraft-foot">${uploadButton("Upload first log")}</div>`;
      } else {
        html += matching.map((entry) => flightItemHtml(entry, aircraft)).join("");
        html += `<div class="fleet-aircraft-foot">${uploadButton("Upload log")}</div>`;
      }
      html += "</div>";
    }
    html += "</div>";
    return html;
  }

  function renderFleetSidebar() {
    const container = sidebarElement();
    if (!container) return;
    if (!newUiFeatureEnabled()) {
      container.innerHTML = "";
      renderNewUiBreadcrumb();
      renderNewUiFirstRun();
      closeFleetDrawer();
      return;
    }
    document.body.classList.toggle("fleet-rail-collapsed", railCollapsed);
    // Account > Settings "Aircraft order". Default is "manual", which returns the
    // list untouched so the existing drag order (aircraft.display_order) still
    // wins; the other modes are computed views over the same fleet.
    // Entries here are tail-number strings, not rows, so the ordering needs
    // accessors to reach the profile (date added) and the flight manifest
    // (most recent activity).
    const aircraftList = orderFleetForDisplay(allAircraftOptions(), undefined, {
      tailOf: (aircraft) => aircraftDisplayLabel(aircraft) || aircraft,
      createdAtOf: (aircraft) => aircraftCreatedAt?.(aircraft) ?? null,
      flightCountOf: (aircraft) => aircraftFlights(aircraft).length,
      activityAtOf: (aircraft) => {
        const newest = latestFirstFlights(aircraft)[0];
        return newest?.flight_start_at ?? newest?.flight_start_local ?? null;
      },
    });
    const singleMode = aircraftList.length === 1;
    // First ever paint: default the accordion to the current aircraft. After
    // that, only explicit clicks change which group is open.
    if (!openTailInitialized) {
      const currentAircraft = selectedAircraftWorkspace();
      if (currentAircraft) setOpenTail(currentAircraft);
    }
    // Brand header: the topbar brand moves into the rail in the New UI. When
    // the rail is collapsed (desktop only, via CSS) just the logo remains and
    // clicking it expands the rail instead of navigating.
    let html = `<div class="fleet-sidebar-head">
      <a class="fleet-brand" href="https://www.alaryiq.com/" target="_blank" rel="noopener" data-fleet-brand aria-label="AlaryIQ home (opens in a new tab)">
        <span class="fleet-brand-mark"><img src="assets/alaryiq-icon-70-v2.png" alt="" /></span>
        <span class="fleet-brand-copy">
          <strong><span class="brand-name-main">Alary</span><span class="brand-name-accent">IQ</span></strong>
          <small>Intelligent Engine Analytics</small>
        </span>
      </a>
      <button class="fleet-collapse-toggle" type="button" data-fleet-collapse
        aria-label="${railCollapsed ? "Expand sidebar" : "Collapse sidebar"}"
        title="${railCollapsed ? "Expand sidebar" : "Collapse sidebar"}">
        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <path d="M15 6l-6 6 6 6" />
        </svg>
      </button>
    </div>`;
    if (!aircraftList.length && !aircraftFlightManifestReady()) {
      // Fleet data is still loading (first authed paint happens before the
      // manifest arrives): show nothing rather than a false first-run state.
      // Landing on views that never load the manifest themselves (My Account,
      // Admin) would sit here forever, so the rail kicks the idempotent load
      // and repaints when it settles.
      kickManifestLoad();
      html += '<div class="fleet-sidebar-empty"><p>Loading fleet...</p></div>';
    } else if (!aircraftList.length) {
      html += `<div class="fleet-sidebar-empty">
        <p>No aircraft yet.<br />Add one to get started.</p>
        <button class="primary-action" type="button" data-fleet-add-aircraft>Add your first aircraft</button>
      </div>`;
    } else {
      html += `<div class="fleet-sidebar-search">
        <input type="search" id="fleetSidebarSearch" placeholder="Search..." aria-label="Search"
          value="${escapeHtml(searchQuery)}" />
        <button class="fleet-search-icon" type="button" data-fleet-search aria-label="Search" title="Search">
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <circle cx="11" cy="11" r="7" />
            <path d="M20 20l-4.2-4.2" />
          </svg>
        </button>
        <button class="fleet-search-clear" type="button" data-fleet-search-clear aria-label="Clear search" title="Clear search" tabindex="-1">
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path d="M6 6l12 12" />
            <path d="M18 6L6 18" />
          </svg>
        </button>
      </div>`;
      html += `<div class="fleet-label">
        <span>${singleMode ? "My Aircraft" : "Fleet"}</span>
      </div>`;
      html += '<div class="fleet-groups">';
      const groups = aircraftList.map((aircraft) => aircraftGroupHtml(aircraft, singleMode)).join("");
      html += groups || '<p class="fleet-flights-empty fleet-search-empty">No flights match the search.</p>';
      html += `<button class="fleet-add-aircraft" type="button" data-fleet-add-aircraft aria-label="Add aircraft" title="Add aircraft">
        <svg class="fleet-add-aircraft-plus" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <path d="M12 5v14" />
          <path d="M5 12h14" />
        </svg>
        <span class="fleet-add-aircraft-text">Add aircraft</span>
      </button>`;
      html += "</div>";
    }
    const accountName = document.querySelector(".topbar-user-name")?.textContent?.trim() || "My Account";
    const initials = accountName.split(/\s+/).map((part) => part[0] ?? "").join("").slice(0, 2).toUpperCase() || "MA";
    // The topbar Admin button is unhidden only for site admins; mirror that.
    const isAdmin = document.querySelector<HTMLButtonElement>("#adminNavButton")?.hidden === false;
    html += `<div class="fleet-sidebar-foot">
      <button class="fleet-sidebar-account" type="button" data-fleet-account aria-label="Open My Account" title="${escapeHtml(accountName)}">
        <span class="fleet-account-avatar" aria-hidden="true">${escapeHtml(initials)}</span>
        <span class="fleet-account-name">My Account</span>
      </button>
      ${isAdmin ? `<button class="fleet-sidebar-admin" type="button" data-fleet-admin aria-label="Admin" title="Admin">
        <svg class="fleet-sidebar-admin-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 3.5 5 6.2v5.1c0 4.2 2.9 7.4 7 9.2 4.1-1.8 7-5 7-9.2V6.2Z" /><path d="M9.3 12.2 11.2 14l3.6-3.8" /></svg>
        <span class="fleet-sidebar-admin-text">Admin</span>
      </button>` : ""}
    </div>`;
    // Re-rendering replaces the .fleet-groups scroll container, which would
    // snap a scrolled fleet list back to the top on every expand/collapse.
    // Carry the scroll offset across the rebuild (clamped by the browser if
    // the new content is shorter).
    const previousScrollTop = container.querySelector(".fleet-groups")?.scrollTop ?? 0;
    container.innerHTML = html;
    if (previousScrollTop) {
      const groupsEl = container.querySelector(".fleet-groups");
      if (groupsEl) groupsEl.scrollTop = previousScrollTop;
    }
    fitAvatarFallbackLabels(container);
    renderNewUiBreadcrumb();
    renderNewUiFirstRun();
  }

  // Size each placeholder tail label by measurement so the full tail number
  // is always visible with breathing room, whatever the glyph widths. Real
  // tails max out around 6 characters ("N12345"), which lands near the top
  // size; longer labels keep shrinking until they fit.
  function fitAvatarFallbackLabels(container: HTMLElement) {
    container.querySelectorAll<HTMLElement>(".fleet-aircraft-avatar-fallback").forEach((el) => {
      let size = 13;
      el.style.fontSize = `${size}px`;
      while (size > 6 && el.scrollWidth > el.clientWidth) {
        size -= 1;
        el.style.fontSize = `${size}px`;
      }
    });
  }

  function crumbHtml(label: string, action = "") {
    if (!action) return `<span class="newui-crumb is-here">${escapeHtml(label)}</span>`;
    return `<button class="newui-crumb is-link" type="button" data-crumb-panel="${escapeHtml(action)}">${escapeHtml(label)}</button>`;
  }

  function renderNewUiBreadcrumb() {
    const container = crumbsElement();
    if (!container) return;
    const separator = '<span class="newui-crumb-sep" aria-hidden="true">/</span>';
    // My Account renders its own crumb ("My Account / Profile") instead of
    // the workspace tail crumb; no fleet needed.
    if (document.body?.dataset?.activeView === "account") {
      if (!newUiFeatureEnabled()) {
        container.innerHTML = "";
        return;
      }
      const tabLabel = document.querySelector(".admin-sidebar-item.is-active[data-account-tab]")?.textContent?.trim() || "Profile";
      container.innerHTML = crumbHtml("My Account") + separator + crumbHtml(tabLabel);
      return;
    }
    // Admin mirrors My Account: "Admin / <tab>" instead of the workspace
    // tail crumb.
    if (document.body?.dataset?.activeView === "admin") {
      if (!newUiFeatureEnabled()) {
        container.innerHTML = "";
        return;
      }
      const tabLabel = document.querySelector(".admin-sidebar-item.is-active[data-admin-tab]")?.textContent?.trim() || "Users";
      container.innerHTML = crumbHtml("Admin") + separator + crumbHtml(tabLabel);
      return;
    }
    if (!newUiFeatureEnabled() || !allAircraftOptions().length) {
      container.innerHTML = "";
      return;
    }
    const aircraft = selectedAircraftWorkspace();
    const tail = aircraftDisplayLabel(aircraft) || aircraft;
    const panel = getSelectedAircraftPanel();
    // The tail crumb opens the aircraft's Flights page (the New UI has no
    // separate profile panel).
    let trail = "";
    if (panel === "dashboard") {
      const flight = getSelectedFlight();
      const flightLabel = flight
        ? [flightDateLabel(flight), flightRouteLabel(flight) ? `(${flightRouteLabel(flight)})` : ""].filter(Boolean).join(" ")
        : "Dashboard";
      trail = crumbHtml(tail, "flights")
        + separator
        + crumbHtml("Flights", "flights")
        + separator
        + crumbHtml(flightLabel || "Dashboard");
    } else if (panel === "flights" || panel === "profile") {
      trail = crumbHtml(tail) + separator + crumbHtml("Flights");
    } else {
      const labels: Record<string, string> = {
        trends: "Trends",
        maintenance: "Maintenance",
        uploads: "Uploads",
        info: "Info",
        settings: "Settings",
      };
      trail = crumbHtml(tail, "flights") + separator + crumbHtml(labels[panel] ?? panel);
    }
    container.innerHTML = trail;
  }

  function renderNewUiFirstRun() {
    const hero = firstRunElement();
    const aircraftView = document.querySelector<HTMLElement>("#view-aircraft");
    if (!hero) return;
    // Only claim "first run" once the manifest has actually loaded; before
    // that an authed member with aircraft would flash the hero over their data.
    const show = newUiFeatureEnabled() && aircraftFlightManifestReady() && !allAircraftOptions().length;
    hero.hidden = !show;
    aircraftView?.classList.toggle("is-newui-firstrun", show);
  }

  function findFlightEntry(aircraft: string, flightId: string) {
    return aircraftFlights(aircraft).find((entry) => entry.id === flightId) ?? null;
  }

  // On small mobile the sidebar is a full-page "Home" overlay (body class
  // mobile-home-active). Navigating to Admin / My Account from that overlay
  // must drop the class so the workspace shows through; the event lets the tab
  // bar re-sync its highlight (Admin / Account own no tab).
  function exitMobileHomeOverlay() {
    if (document.body.classList.contains("mobile-home-active")) {
      document.body.classList.remove("mobile-home-active");
      window.dispatchEvent(new CustomEvent("aeroinsight:mobile-home-exit"));
    }
  }

  function handleSidebarClick(event: Event) {
    const target = event.target as HTMLElement;
    const actionable = target.closest<HTMLElement>(
      "[data-fleet-flight],[data-fleet-toggle],[data-fleet-aircraft],[data-fleet-viewall],[data-fleet-upload],[data-fleet-upload-first],[data-fleet-add-aircraft],[data-fleet-account],[data-fleet-admin],[data-fleet-collapse],[data-fleet-brand],[data-fleet-search],[data-fleet-search-clear],[data-crumb-panel]",
    );
    if (!actionable) return;

    if (actionable.dataset.fleetCollapse !== undefined) {
      setRailCollapsed(!railCollapsed);
      return;
    }
    if (actionable.dataset.fleetBrand !== undefined) {
      // Collapsed rail (desktop): the logo is the expand control, not a link.
      if (railCollapsed && window.innerWidth > 900) {
        event.preventDefault();
        setRailCollapsed(false);
      }
      return;
    }

    if (actionable.dataset.fleetAdmin !== undefined) {
      exitMobileHomeOverlay();
      document.querySelector<HTMLButtonElement>("#adminNavButton")?.click();
      closeFleetDrawer();
      return;
    }
    if (actionable.dataset.fleetAccount !== undefined) {
      // Reuse the (hidden) topbar user button so account-view activation
      // stays in one place.
      exitMobileHomeOverlay();
      document.querySelector<HTMLButtonElement>(".topbar-user")?.click();
      closeFleetDrawer();
      return;
    }

    if (actionable.dataset.fleetUploadFirst !== undefined) {
      // First-run upload: no aircraft exists yet, so the upload dialog opens
      // in its create-new-aircraft mode. Drop the full-page Home overlay first
      // so the dialog isn't opened behind it.
      exitMobileHomeOverlay();
      document.querySelector<HTMLButtonElement>("#aircraftUploadButton")?.click();
      return;
    }
    if (actionable.dataset.fleetAddAircraft !== undefined) {
      // Reuse the existing header button so the read-only and plan-limit gates
      // stay in one place. Stay on the full-page Home — the add-aircraft (and
      // plan-limit) dialog is a top-layer modal, so it shows above Home, and
      // closing it returns here.
      document.querySelector<HTMLButtonElement>("#aircraftHeaderAddButton")?.click();
      closeFleetDrawer();
      return;
    }
    if (actionable.dataset.fleetSearchClear !== undefined) {
      // Clear the search and re-render the full fleet, keeping focus in the box.
      searchQuery = "";
      renderFleetSidebar();
      window.setTimeout(() => document.querySelector<HTMLInputElement>("#fleetSidebarSearch")?.focus(), 0);
      return;
    }
    if (actionable.dataset.fleetSearch !== undefined) {
      // Collapsed rail: the search icon expands the rail and focuses the box.
      setRailCollapsed(false);
      window.setTimeout(() => document.querySelector<HTMLInputElement>("#fleetSidebarSearch")?.focus(), 0);
      return;
    }
    if (actionable.dataset.fleetToggle) {
      // Chevron / expand strip: pure open-close of that section (accordion:
      // opening one closes the others). Never changes the selected aircraft.
      const tail = actionable.dataset.fleetToggle;
      setOpenTail(openTail === tail ? null : tail);
      renderFleetSidebar();
      return;
    }
    if (actionable.dataset.fleetFlight) {
      const entry = findFlightEntry(actionable.dataset.fleetFlightAircraft ?? "", actionable.dataset.fleetFlight);
      if (!entry || !isSupportedFlight(entry) || loadingFlightId) return;
      // Accordion follows the flight's aircraft (matters when a search shows
      // several open groups at once).
      if (entry.aircraft) setOpenTail(entry.aircraft);
      closeFleetDrawer();
      loadingFlightId = entry.id;
      renderFleetSidebar();
      const status = document.createElement("div");
      status.className = "fleet-flight-loading-status";
      status.setAttribute("role", "status");
      status.textContent = "Loading flight...";
      document.body.append(status);
      document.body.classList.add("is-fleet-flight-loading");
      void (async () => {
        try {
          await openFleetSidebarFlight(entry);
        } catch {
          showAppToast("Couldn't load this flight. Click the flight to retry.");
        } finally {
          loadingFlightId = null;
          status.remove();
          document.body.classList.remove("is-fleet-flight-loading");
          renderFleetSidebar();
        }
      })();
      return;
    }
    if (actionable.dataset.fleetViewall) {
      openFleetSidebarPanel(actionable.dataset.fleetViewall, "flights");
      closeFleetDrawer();
      return;
    }
    if (actionable.dataset.fleetUpload) {
      // "Add log" takes you to the aircraft's page (Flights), then opens the
      // upload dialog there.
      openFleetSidebarPanel(actionable.dataset.fleetUpload, "flights");
      document.querySelector<HTMLButtonElement>("#aircraftUploadButton")?.click();
      closeFleetDrawer();
      return;
    }
    if (actionable.dataset.crumbPanel) {
      openFleetSidebarPanel(selectedAircraftWorkspace(), actionable.dataset.crumbPanel);
      return;
    }
    if (actionable.dataset.fleetAircraft) {
      const aircraft = actionable.dataset.fleetAircraft;
      if (aircraftWorkspacePlanLocked(aircraft)) {
        showAppToast("This aircraft is not available on the current plan.");
        return;
      }
      // Clicking an aircraft always selects it, opens its section (the
      // chevron owns collapsing; accordion: any other open section closes),
      // and lands on its aircraft page, even when that aircraft's dashboard
      // or another panel is already showing. Switching aircraft keeps the
      // current aircraft page tab (Uploads to Uploads, Trends to Trends, ...);
      // from anywhere else, "profile" is the aircraft page default in both
      // UIs (the workspace maps it to the New UI equivalent).
      const currentPanel = getSelectedAircraftPanel();
      const keepPanel = ["flights", "uploads", "trends", "info", "settings", "maintenance"].includes(currentPanel)
        ? currentPanel
        : "profile";
      if (openFleetSidebarPanel(aircraft, keepPanel) === false) return;
      setOpenTail(aircraft);
      renderFleetSidebar();
      closeFleetDrawer();
    }
  }

  function handleSidebarKeydown(event: KeyboardEvent) {
    if (event.key !== "Enter" && event.key !== " ") return;
    const head = (event.target as HTMLElement).closest<HTMLElement>(".fleet-aircraft-head");
    if (!head) return;
    event.preventDefault();
    head.click();
  }

  function handleSidebarInput(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input?.id !== "fleetSidebarSearch") return;
    searchQuery = input.value ?? "";
    const caret = input.selectionStart;
    renderFleetSidebar();
    const restored = document.querySelector<HTMLInputElement>("#fleetSidebarSearch");
    if (restored) {
      restored.focus();
      if (caret !== null) restored.setSelectionRange(caret, caret);
    }
  }

  function wireFleetSidebar() {
    if (wired) return;
    wired = true;
    // Publish the topbar's rendered height as --topbar-h so sticky elements
    // (the account/admin rail) can pin flush under it. A hardcoded offset
    // can exceed the rail's natural resting position, which makes sticky
    // shove it down immediately and misalign it with the hero panel.
    const topbar = document.querySelector<HTMLElement>(".topbar");
    if (topbar) {
      const syncTopbarHeightVar = () => {
        const height = Math.round(topbar.getBoundingClientRect().height);
        if (height > 0) document.documentElement.style.setProperty("--topbar-h", `${height}px`);
      };
      syncTopbarHeightVar();
      if (typeof ResizeObserver !== "undefined") {
        new ResizeObserver(syncTopbarHeightVar).observe(topbar);
      }
    }
    sidebarElement()?.addEventListener("click", handleSidebarClick);
    sidebarElement()?.addEventListener("keydown", handleSidebarKeydown);
    sidebarElement()?.addEventListener("input", handleSidebarInput);
    crumbsElement()?.addEventListener("click", handleSidebarClick);
    firstRunElement()?.addEventListener("click", handleSidebarClick);
    document.querySelector("#fleetSidebarToggle")?.addEventListener("click", () => {
      document.body.classList.toggle("fleet-drawer-open");
    });
    document.querySelector("#fleetSidebarScrim")?.addEventListener("click", closeFleetDrawer);
    // Repaint when the admin flag resolves so the footer Admin button appears
    // without waiting for the next workspace render.
    window.addEventListener("aeroinsight:admin-nav-visibility", () => renderFleetSidebar());
    // Keep the breadcrumb current on view switches (MutationObserver: fires
    // as a microtask, no frames needed) and on account tab changes.
    if (typeof MutationObserver !== "undefined") {
      new MutationObserver(() => renderNewUiBreadcrumb())
        .observe(document.body, { attributes: true, attributeFilter: ["data-active-view"] });
    }
    window.addEventListener("aeroinsight:account-tab", () => setTimeout(renderNewUiBreadcrumb, 0));
    document.addEventListener("click", (event) => {
      if ((event.target as HTMLElement)?.closest?.("[data-account-tab],[data-admin-tab]")) setTimeout(renderNewUiBreadcrumb, 0);
    });
  }

  return {
    renderFleetSidebar,
    wireFleetSidebar,
  };
}
